import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getUserProfile,
  uploadInvoiceToDrive,
  formatDriveUploadError,
} from '../api';
import { EMPTY_COMPANY_PREFERENCES } from '../types';
import {
  COUNTRY_OPTIONS,
  withCurrentOption,
  getOptionLabel,
} from '../utils/companyPreferenceOptions';
import { useCompanyPreferences } from '../hooks/useCompanyPreferences';
import { CurrencySelect } from '../components/CurrencySelect';
import { getCompanyInvoices } from '@/modules/Authentication';
import { MediaViewerModal } from '@/common/components/MediaViewer/MediaViewerModal';
import { Button, CustomSelect, StatusBadge, Switch } from '@/components';
import { env } from '@/common/config';
import { usePermissions } from '@/common/hooks';
import {
  DashboardPage,
  DashboardCard,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from '../components/PremiumDesign';
import { ArrowDown, EyeIcon } from '../components/Icons';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { useOneSignal } from '@/common/hooks/useOneSignal';
import { ProfileSettingsSkeleton } from '../components/ProfileSettingsSkeleton';
import notificationIcon from '@/assets/notification.png';

const INVOICE_STATUS_TONE = {
  paid: 'success',
  Paid: 'success',
  PAID: 'success',
  captured: 'success',
  pending: 'warning',
  Pending: 'warning',
  PENDING: 'warning',
  failed: 'error',
  Failed: 'error',
  FAILED: 'error',
};

function normalizeInvoices(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.invoices)) return data.invoices;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

function formatInvoiceAmount(amount, currency = 'INR') {
  const num = Number(amount);
  if (Number.isNaN(num)) return amount || '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `₹${num.toFixed(2)}`;
  }
}

function formatInvoiceDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function resolveInvoicePdfUrl(rawPdfUrl) {
  if (!rawPdfUrl) return null;
  return rawPdfUrl.startsWith('http')
    ? rawPdfUrl
    : `${env.photoBaseUrl}${rawPdfUrl}`;
}

async function downloadInvoicePdf(pdfUrl, fileName) {
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error('Failed to download invoice PDF');
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export function SettingsPage() {
  const { role } = usePermissions();
  const isOwner = String(role || '').toLowerCase() === 'owner';

  const {
    ready: isOneSignalReady,
    configured: isOneSignalConfigured,
    supported: isOneSignalSupported,
    permissionStatus: pushPermissionStatus,
    isOptedIn,
    enableNotifications,
    disableNotifications,
  } = useOneSignal();

  const [isTogglingPush, setIsTogglingPush] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState(null);

  const { mutate: uploadInvoice } = useMutation({
    mutationFn: ({ invoiceId }) =>
      uploadInvoiceToDrive({ companyId, invoiceId }),
    onMutate: ({ invoiceId }) => setUploadingInvoiceId(invoiceId),
    onSuccess: (data) => {
      setUploadingInvoiceId(null);
      if (data?.webViewLink) {
        toast.success(
          <span>
            Uploaded to Drive!{' '}
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              View in Drive
            </a>
          </span>,
          { duration: 6000 },
        );
      } else {
        toast.success('Invoice uploaded to Google Drive successfully.');
      }
    },
    onError: (err) => {
      setUploadingInvoiceId(null);
      toast.error(
        formatDriveUploadError(err, 'Failed to upload invoice to Drive.'),
      );
    },
  });

  const handleTogglePush = async (nextValue) => {
    if (
      isTogglingPush ||
      (!isOneSignalReady && pushPermissionStatus !== 'granted')
    )
      return;
    setIsTogglingPush(true);
    try {
      if (nextValue) {
        const res = await enableNotifications();
        if (res === 'granted' || res === true) {
          toast.success('Push notifications enabled!');
        } else if (res === 'denied') {
          toast.error(
            'Notifications are blocked in browser settings. Please enable permission in your address bar.',
          );
        }
      } else {
        const ok = await disableNotifications();
        if (ok) {
          toast.success('Push notifications disabled.');
        } else {
          toast.error('Failed to disable push notifications.');
        }
      }
    } catch {
      toast.error('Failed to update push notification setting.');
    } finally {
      setIsTogglingPush(false);
    }
  };

  const [preferencesForm, setPreferencesForm] = useState(
    EMPTY_COMPANY_PREFERENCES,
  );
  const [showPreferenceErrors, setShowPreferenceErrors] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: getUserProfile,
  });

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = profile?.company_id || getCompanyId();

  const {
    preferences,
    isPending: isPreferencesPending,
    isError: isPreferencesError,
    error: preferencesError,
    refetch: refetchPreferences,
    update: updatePreferences,
    isUpdating: isUpdatingPreferences,
  } = useCompanyPreferences(companyId);

  const { data: invoicesData, isLoading: isCompanyInvoicesLoading } = useQuery({
    queryKey: ['company-invoices', companyId],
    queryFn: () => getCompanyInvoices(companyId),
    enabled: !!companyId && isOwner,
  });

  const invoices = normalizeInvoices(invoicesData);

  const savedCountry = preferences?.country || '';
  const savedCurrency = preferences?.currency || '';
  const savedLanguage = preferences?.language || '';
  const hasPreferencesData = Boolean(preferences);

  useEffect(() => {
    if (!hasPreferencesData) return;
    setPreferencesForm({
      country: savedCountry,
      currency: savedCurrency,
      language: savedLanguage,
    });
    setShowPreferenceErrors(false);
  }, [hasPreferencesData, savedCountry, savedCurrency, savedLanguage]);

  const isLoadingPreferences = Boolean(companyId) && isPreferencesPending;
  const preferencesFetchError = isPreferencesError ? preferencesError : null;

  const showSkeleton = useDelayedSkeleton(
    isLoadingPreferences || (Boolean(companyId) && isCompanyInvoicesLoading),
  );

  const hasPreferencesChanges = useMemo(
    () =>
      preferencesForm.country !== savedCountry ||
      preferencesForm.currency !== savedCurrency ||
      preferencesForm.language !== savedLanguage,
    [preferencesForm, savedCountry, savedCurrency, savedLanguage],
  );

  const preferenceFieldErrors = useMemo(
    () => ({
      language: preferencesForm.language ? '' : 'Language is required',
      currency: preferencesForm.currency ? '' : 'Currency is required',
      country: preferencesForm.country ? '' : 'Country is required',
    }),
    [preferencesForm],
  );

  const isPreferencesValid = !Object.values(preferenceFieldErrors).some(
    Boolean,
  );

  const countryOptions = useMemo(
    () => withCurrentOption(COUNTRY_OPTIONS, preferencesForm.country),
    [preferencesForm.country],
  );

  const handlePreferenceChange = (field, value) => {
    setPreferencesForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSavePreferences = () => {
    if (!isOwner) return;
    if (!isPreferencesValid) {
      setShowPreferenceErrors(true);
      return;
    }
    if (!hasPreferencesChanges || isUpdatingPreferences) return;
    updatePreferences({
      country: preferencesForm.country,
      currency: preferencesForm.currency,
      language: preferencesForm.language,
    });
  };

  const handleViewInvoice = async (invoice) => {
    const rawPdfUrl = invoice.pdf_url || invoice.invoice_pdf || null;
    const pdfUrl = resolveInvoicePdfUrl(rawPdfUrl);
    if (!pdfUrl) {
      toast.error('Invoice PDF is not available yet');
      return;
    }
    const fileName =
      invoice.receipt_number ||
      invoice.invoice_number ||
      `invoice-${invoice.id ?? invoice.invoice_id ?? 'preview'}`;
    const displayName = fileName.endsWith('.pdf')
      ? fileName
      : `${fileName}.pdf`;

    setPreviewInvoice({
      invoice,
      pdfUrl: null,
      fileName: displayName,
      loading: true,
    });

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error('Failed to load invoice PDF');
      }
      const rawBlob = await response.blob();
      const pdfBlob = new Blob([rawBlob], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPreviewInvoice({
        invoice,
        pdfUrl: blobUrl,
        fileName: displayName,
        loading: false,
      });
    } catch (err) {
      setPreviewInvoice(null);
      toast.error(err?.message || 'Failed to open invoice preview');
    }
  };

  const handleCloseInvoicePreview = () => {
    setPreviewInvoice((current) => {
      if (current?.pdfUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(current.pdfUrl);
      }
      return null;
    });
  };

  const handleDownloadInvoice = async (invoice) => {
    const invoiceKey =
      invoice.id ?? invoice.invoice_id ?? invoice.receipt_number;
    const rawPdfUrl = invoice.pdf_url || invoice.invoice_pdf || null;
    const pdfUrl = resolveInvoicePdfUrl(rawPdfUrl);
    if (!pdfUrl || downloadingInvoiceId === invoiceKey) return;

    const fileName =
      invoice.receipt_number ||
      invoice.invoice_number ||
      `invoice-${invoiceKey || 'download'}`;

    setDownloadingInvoiceId(invoiceKey);
    const toastId = toast.loading('Downloading invoice...');
    try {
      await downloadInvoicePdf(pdfUrl, fileName);
      toast.success('Invoice downloaded', { id: toastId });
    } catch (err) {
      toast.error(err?.message || 'Failed to download invoice', {
        id: toastId,
      });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  if (showSkeleton) {
    return <ProfileSettingsSkeleton />;
  }

  return (
    <DashboardPage className="!p-0 !bg-transparent">
      <div className="flex flex-col gap-6 animate-fadeIn w-full pb-6">
        {companyId ? (
          <DashboardCard
            className="flex flex-col bg-white border border-border-default shadow-sm"
            elevated={false}
          >
            <div className="flex items-center justify-between mb-3 gap-3">
              <h3 className="text-xl font-medium text-primary">Preferences</h3>
              {isOwner && !isLoadingPreferences && !preferencesFetchError ? (
                <Button
                  variant="primary"
                  onClick={handleSavePreferences}
                  disabled={
                    !hasPreferencesChanges ||
                    isUpdatingPreferences ||
                    isLoadingPreferences
                  }
                  className="px-5 py-2.5 h-[40px] text-[14px] font-medium rounded-lg shrink-0"
                >
                  {isUpdatingPreferences ? 'Saving...' : 'Save'}
                </Button>
              ) : null}
            </div>

            {preferencesFetchError ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-[15px] text-text-muted">
                  {preferencesFetchError?.response?.data?.detail ||
                    preferencesFetchError?.message ||
                    'Failed to load preferences.'}
                </p>
                <Button
                  variant="secondary"
                  onClick={() => refetchPreferences()}
                  className="h-[40px] px-5 text-[14px] shrink-0"
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-8">
                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Currency
                  </span>
                  {isOwner ? (
                    <>
                      <CurrencySelect
                        value={preferencesForm.currency}
                        onChange={(value) =>
                          handlePreferenceChange('currency', value)
                        }
                        disabled={isUpdatingPreferences}
                        error={
                          showPreferenceErrors &&
                          Boolean(preferenceFieldErrors.currency)
                        }
                      />
                      {showPreferenceErrors &&
                      preferenceFieldErrors.currency ? (
                        <span className="text-status-error text-sm font-medium mt-1 block">
                          {preferenceFieldErrors.currency}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <CurrencySelect value={preferencesForm.currency} readOnly />
                  )}
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Country
                  </span>
                  {isOwner ? (
                    <>
                      <CustomSelect
                        value={preferencesForm.country}
                        onChange={(value) =>
                          handlePreferenceChange('country', value)
                        }
                        options={countryOptions}
                        placeholder="Select country"
                        disabled={isUpdatingPreferences}
                        searchable
                        searchPlaceholder="Search country..."
                        error={
                          showPreferenceErrors &&
                          Boolean(preferenceFieldErrors.country)
                        }
                        className="w-full h-[40px] sm:h-[44px]"
                      />
                      {showPreferenceErrors && preferenceFieldErrors.country ? (
                        <span className="text-status-error text-sm font-medium mt-1 block">
                          {preferenceFieldErrors.country}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-[15px] font-medium text-black">
                      {getOptionLabel(countryOptions, preferencesForm.country)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </DashboardCard>
        ) : null}

        {isOneSignalConfigured && isOneSignalSupported ? (
          <DashboardCard
            className="flex flex-col bg-white border border-border-default shadow-sm"
            elevated={false}
          >
            <div className="flex items-center justify-between mb-2 gap-3">
              <div>
                <h3 className="text-xl font-medium text-primary">
                  Push Notifications
                </h3>
                <p className="text-sm text-text-muted mt-0.5">
                  Manage web push notification alerts for messages and updates.
                </p>
              </div>
              {pushPermissionStatus === 'granted' && isOptedIn ? (
                <StatusBadge
                  tone="success"
                  className="text-xs px-2.5 py-1 font-medium shrink-0"
                >
                  Enabled
                </StatusBadge>
              ) : pushPermissionStatus === 'granted' && !isOptedIn ? (
                <StatusBadge
                  tone="warning"
                  className="text-xs px-2.5 py-1 font-medium shrink-0"
                >
                  Disabled
                </StatusBadge>
              ) : pushPermissionStatus === 'denied' ? (
                <StatusBadge
                  tone="error"
                  className="text-xs px-2.5 py-1 font-medium shrink-0"
                >
                  Blocked
                </StatusBadge>
              ) : !isOneSignalReady ? (
                <StatusBadge
                  tone="error"
                  className="text-xs px-2.5 py-1 font-medium shrink-0"
                >
                  Init Error
                </StatusBadge>
              ) : (
                <StatusBadge
                  tone="warning"
                  className="text-xs px-2.5 py-1 font-medium shrink-0"
                >
                  Not Enabled
                </StatusBadge>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-layer-1-page border border-border-default mt-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <img
                    src={notificationIcon}
                    alt="Notification"
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-medium text-text-primary">
                    Desktop & Browser Alerts
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5 break-words">
                    {!isOneSignalReady && pushPermissionStatus !== 'granted'
                      ? 'Connecting to push service...'
                      : pushPermissionStatus === 'denied'
                        ? 'Notifications are blocked by your browser. Click the settings/lock icon next to localhost:3000 in your browser address bar, set Notifications to Allow, and refresh the page.'
                        : pushPermissionStatus === 'granted' && isOptedIn
                          ? 'Push notifications are active for this browser session.'
                          : pushPermissionStatus === 'granted' && !isOptedIn
                            ? 'Push notifications are disabled for this browser. Turn them back on anytime.'
                            : 'Get real-time browser alerts for quotations, chats, and system updates.'}
                  </p>
                </div>
              </div>

              <div
                className="flex items-center gap-3 shrink-0"
                onClick={() => {
                  if (pushPermissionStatus === 'denied') {
                    toast.error(
                      'Browser blocked notifications! Click the icon next to the URL in your address bar to Allow, then reload.',
                    );
                  }
                }}
              >
                <Switch
                  checked={
                    pushPermissionStatus === 'granted' && Boolean(isOptedIn)
                  }
                  disabled={
                    (!isOneSignalReady && pushPermissionStatus !== 'granted') ||
                    isTogglingPush ||
                    pushPermissionStatus === 'denied'
                  }
                  onChange={(checked) => handleTogglePush(checked)}
                  aria-label="Toggle push notifications"
                />
              </div>
            </div>
          </DashboardCard>
        ) : null}

        {isOwner && invoices.length > 0 ? (
          <DashboardCard
            className="flex flex-col bg-white border border-border-default shadow-sm"
            elevated={false}
          >
            <div className="mb-4">
              <h3 className="text-lg text-primary leading-tight mb-1">
                Invoices
              </h3>
              <p className="text-sm text-text-muted leading-snug">
                View and download invoices for your company subscriptions.
              </p>
            </div>

            <DashboardTable className="min-w-[600px]">
              <DashboardTableHeader>
                <th className="py-3 px-4 text-left">Number</th>
                <th className="py-3 px-4 text-left">Plan</th>
                <th className="py-3 px-4 text-left">Issued</th>
                <th className="py-3 px-4 text-left">Amount</th>
                <th className="py-3 px-4 text-left">Status</th>
                <th className="py-3 px-4 text-left">Actions</th>
              </DashboardTableHeader>
              <DashboardTableBody>
                {invoices.map((invoice, index) => {
                  const invoiceId =
                    invoice.id ??
                    invoice.invoice_id ??
                    invoice.receipt_number ??
                    index;
                  const receiptLabel =
                    invoice.receipt_number ||
                    invoice.invoice_number ||
                    invoice.invoiceId ||
                    `Invoice #${invoiceId}`;
                  const status = invoice.status || 'Pending';
                  const statusLabel =
                    String(status).charAt(0).toUpperCase() +
                    String(status).slice(1);
                  const amount = formatInvoiceAmount(
                    invoice.amount ??
                      invoice.total_amount ??
                      invoice.amount_paid,
                    invoice.currency,
                  );
                  const date = formatInvoiceDate(
                    invoice.created_at ||
                      invoice.issued_at ||
                      invoice.paid_at ||
                      invoice.date,
                  );
                  const planName =
                    invoice.plan_name ||
                    invoice.plan ||
                    invoice.billing_period ||
                    '—';
                  const rawPdfUrl =
                    invoice.pdf_url || invoice.invoice_pdf || null;
                  const pdfUrl = resolveInvoicePdfUrl(rawPdfUrl);
                  const isDownloading = downloadingInvoiceId === invoiceId;
                  const canOpenPdf = Boolean(pdfUrl);

                  return (
                    <DashboardTableRow
                      key={invoiceId}
                      className="cursor-default"
                      onClick={undefined}
                    >
                      <DashboardTableCell className="font-medium">
                        {receiptLabel}
                      </DashboardTableCell>
                      <DashboardTableCell className="text-text-muted">
                        {planName}
                      </DashboardTableCell>
                      <DashboardTableCell>{date}</DashboardTableCell>
                      <DashboardTableCell className="font-medium">
                        {amount}
                      </DashboardTableCell>
                      <DashboardTableCell>
                        <StatusBadge
                          tone={INVOICE_STATUS_TONE[status] || 'neutral'}
                          className="text-xs px-2 py-0.5"
                        >
                          {statusLabel}
                        </StatusBadge>
                      </DashboardTableCell>
                      <DashboardTableCell>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewInvoice(invoice)}
                            disabled={!canOpenPdf}
                            title={
                              canOpenPdf
                                ? 'View invoice PDF'
                                : 'PDF not available yet'
                            }
                            className="p-2 text-primary hover:bg-[#EDF5EE] rounded-lg border border-primary/30 transition-all duration-200 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <EyeIcon className="w-4 h-4 text-primary hover:text-primary" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadInvoice(invoice)}
                            disabled={!canOpenPdf || isDownloading}
                            title={
                              canOpenPdf
                                ? 'Download invoice PDF'
                                : 'PDF not available yet'
                            }
                            className="p-2 text-primary hover:bg-[#EDF5EE] rounded-lg border border-primary/30 transition-all duration-200 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          {invoiceId && (
                            <button
                              type="button"
                              id={`invoice-drive-upload-${invoiceId}`}
                              disabled={uploadingInvoiceId === invoiceId}
                              onClick={() => uploadInvoice({ invoiceId })}
                              title="Upload to Google Drive"
                              className="p-2 hover:bg-yellow-50 rounded-lg border border-yellow-300/60 transition-all duration-200 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {uploadingInvoiceId === invoiceId ? (
                                <svg
                                  className="w-4 h-4 animate-spin text-yellow-600"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                >
                                  <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                                  <path d="M12 3a9 9 0 0 1 9 9" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" className="w-4 h-4">
                                  <path
                                    fill="#FBBC05"
                                    d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z"
                                  />
                                  <path
                                    fill="#4285F4"
                                    d="M22.85 15L16.29 3.5H9.15L15.71 15z"
                                  />
                                  <path
                                    fill="#34A853"
                                    d="M4.58 21h14.84l3.43-6H1.15z"
                                  />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </DashboardTableCell>
                    </DashboardTableRow>
                  );
                })}
              </DashboardTableBody>
            </DashboardTable>
          </DashboardCard>
        ) : null}

        <MediaViewerModal
          isOpen={Boolean(previewInvoice)}
          onClose={handleCloseInvoicePreview}
          mediaList={
            previewInvoice && previewInvoice.pdfUrl
              ? [
                  {
                    id:
                      previewInvoice.invoice.id ||
                      previewInvoice.invoice.invoice_id ||
                      previewInvoice.invoice.receipt_number,
                    mediaUrl: previewInvoice.pdfUrl,
                    fileName: previewInvoice.fileName,
                    mimeType: 'application/pdf',
                    time: new Date().toLocaleString(),
                  },
                ]
              : []
          }
          initialIndex={0}
        />
      </div>
    </DashboardPage>
  );
}
